import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { markerSize, vehicleMarker } from "./car.ts";
import { clampView, decodePolyline, nearestIndex, projector, zoomAt, type LatLng, type MapView, type View } from "./trip.ts";

const KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY;
const BRASA = "#FF6B1A", DONE = "#B3AAA0", INK = "#15110E";

/**
 * Google Maps when a browser key is configured, otherwise the route drawn as SVG (no tiles, no key).
 * Both pan and zoom; once the viewer moves the map it stops following the car until "Centralizar".
 */
export function TripMap({ view }: { view: View }) {
  const route = useMemo(() => (view.polyline ? decodePolyline(view.polyline) : []), [view.polyline]);
  const [failed, setFailed] = useState(false);
  const [moved, setMoved] = useState(false);
  const [recenter, setRecenter] = useState(0);
  const props = { route, view, moved, recenter, onMoved: () => setMoved(true) };
  return (
    <>
      {KEY && !failed ? <GoogleTripMap {...props} onFail={() => setFailed(true)} /> : <SvgTripMap {...props} />}
      {moved && (
        <button type="button" className="glass chip-recenter" onClick={() => { setMoved(false); setRecenter((n) => n + 1); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3.5" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /><circle cx="12" cy="12" r="7.5" />
          </svg>
          Centralizar
        </button>
      )}
    </>
  );
}

type MapProps = { route: LatLng[]; view: View; moved: boolean; recenter: number; onMoved: () => void };

/** Splits the route where the car is: behind it (grey) and still to go (brasa). */
function split(route: LatLng[], car: LatLng | null, status: View["status"]) {
  if (!route.length) return { done: [] as LatLng[], todo: [] as LatLng[] };
  if (status === "completed") return { done: route, todo: [] };
  if (!car || status !== "in_progress") return { done: [], todo: route };
  const i = nearestIndex(route, car);
  return { done: route.slice(0, i + 1), todo: route.slice(i) };
}

const IDENTITY: MapView = { s: 1, tx: 0, ty: 0 };

function SvgTripMap({ route, view, moved, recenter, onMoved }: MapProps) {
  // Draw in the box's own pixels so the route is fitted to what's visible, not cropped by a fixed aspect.
  const el = useRef<SVGSVGElement>(null);
  const [{ W, H }, setBox] = useState({ W: 390, H: 400 });
  const [mv, setMv] = useState<MapView>(IDENTITY);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => e && e.contentRect.width && setBox({ W: e.contentRect.width, H: e.contentRect.height }));
    ro.observe(el.current!);
    return () => ro.disconnect();
  }, []);
  useEffect(() => setMv(IDENTITY), [recenter]);

  const car = view.driverLocation;
  const pts = car ? [...route, car] : route;
  const fitted = pts.length ? projector(pts, W, H, 44) : null;
  // While the viewer has moved the map, keep the projection it had so new positions don't shift it.
  const frozen = useRef(fitted);
  if (!moved || !frozen.current) frozen.current = fitted;
  const base = frozen.current;
  const to = base ? (p: LatLng) => { const { x, y } = base(p); return { x: x * mv.s + mv.tx, y: y * mv.s + mv.ty }; } : null;

  const update = (next: MapView) => {
    setMv(clampView(next, W, H));
    onMoved();
  };

  // Pointers: one pans, two pinch (zooming about their midpoint, panning with it).
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const start = useRef<{ mv: MapView; mid: { x: number; y: number }; dist: number } | null>(null);
  const local = (e: { clientX: number; clientY: number }) => {
    const r = el.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const baseline = () => {
    const ps = [...pointers.current.values()];
    if (!ps.length) return void (start.current = null);
    const mid = { x: ps.reduce((a, p) => a + p.x, 0) / ps.length, y: ps.reduce((a, p) => a + p.y, 0) / ps.length };
    const dist = ps.length > 1 ? Math.hypot(ps[0]!.x - ps[1]!.x, ps[0]!.y - ps[1]!.y) : 0;
    start.current = { mv, mid, dist };
  };
  const down = (e: ReactPointerEvent<SVGSVGElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, local(e));
    baseline();
  };
  const move = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (!pointers.current.has(e.pointerId) || !start.current) return;
    pointers.current.set(e.pointerId, local(e));
    const ps = [...pointers.current.values()];
    const b = start.current;
    const mid = { x: ps.reduce((a, p) => a + p.x, 0) / ps.length, y: ps.reduce((a, p) => a + p.y, 0) / ps.length };
    if (Math.abs(mid.x - b.mid.x) + Math.abs(mid.y - b.mid.y) < 3 && ps.length < 2) return; // a tap, not a pan
    const k = ps.length > 1 && b.dist > 0 ? Math.hypot(ps[0]!.x - ps[1]!.x, ps[0]!.y - ps[1]!.y) / b.dist : 1;
    const z = zoomAt(b.mv, b.mid, k);
    update({ s: z.s, tx: z.tx + mid.x - b.mid.x, ty: z.ty + mid.y - b.mid.y });
  };
  const up = (e: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    baseline();
  };
  // The wheel (and trackpad pinch, which arrives as ctrl+wheel) zooms about the cursor. Not passive, to stop the page zooming.
  const latest = useRef({ mv, update });
  latest.current = { mv, update };
  useEffect(() => {
    const svg = el.current!;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { mv, update } = latest.current;
      update(zoomAt(mv, local(e), Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.002))));
    };
    svg.addEventListener("wheel", onWheel, { passive: false });
    return () => svg.removeEventListener("wheel", onWheel);
  }, []);

  const { done, todo } = split(route, car, view.status);
  const path = (ps: LatLng[]) => ps.map((p, i) => { const { x, y } = to!(p); return `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`; }).join(" ");
  const startPt = route[0] && to ? to(route[0]) : null;
  const end = route.at(-1) && to ? to(route.at(-1)!) : null;
  const c = car && to ? to(car) : { x: W / 2, y: H / 2 };
  const size = markerSize(view.driver?.vehicle.kind);
  return (
    <svg
      ref={el} className="map-svg" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={car ? "Mapa com a posição do motorista e a rota" : "Mapa da rota"}
      onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
      onDoubleClick={(e) => update(zoomAt(mv, local(e), 2))}
    >
      {/* A quiet street grid behind the real route, like the canvas artboard, cropped to fill. */}
      <rect width={W} height={H} fill="#ECE7E1" />
      <g transform={`translate(${mv.tx} ${mv.ty}) scale(${mv.s})`}>
        <svg width={W} height={H} viewBox="0 0 390 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <rect x="230" y="40" width="130" height="90" rx="16" fill="#DCE7D2" />
          <g fill="#F4F0EB"><rect x="30" y="220" width="110" height="70" rx="6" /><rect x="230" y="220" width="120" height="70" rx="6" /></g>
          <g stroke="#FFFFFF" strokeWidth="14" strokeLinecap="round" fill="none"><path d="M-10 180 H400" /><path d="M190 -10 V410" /></g>
          <g stroke="#FFFFFF" strokeWidth="6" fill="none"><path d="M-10 70 H400" /><path d="M-10 320 H400" /><path d="M40 -10 V410" /><path d="M340 -10 V410" /></g>
        </svg>
      </g>
      {done.length > 1 && <path d={path(done)} stroke={DONE} strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" />}
      {todo.length > 1 && <path d={path(todo)} stroke={BRASA} strokeWidth="6" fill="none" strokeLinejoin="round" strokeLinecap="round" />}
      {startPt && <circle cx={startPt.x} cy={startPt.y} r="7" fill="#FFFFFF" stroke={INK} strokeWidth="4" />}
      {end && <rect x={end.x - 10} y={end.y - 10} width="20" height="20" rx="5" fill={INK} stroke="#FFFFFF" strokeWidth="4" />}
      {(car || !route.length) && view.status !== "completed" && (
        <g transform={`translate(${c.x} ${c.y})`}>
          <circle r="22" fill="rgba(255,107,26,.3)" className="ping" />
          <image href={vehicleMarker(view.driver?.vehicle.kind, view.driver?.vehicle.color, car?.heading)} x={-size / 2} y={-size / 2} width={size} height={size} />
        </g>
      )}
    </svg>
  );
}

type G = any; // google.maps, loaded at runtime (no @types/google.maps dependency for one page)
let loader: Promise<G> | null = null;
function loadGoogle(): Promise<G> {
  loader ??= new Promise((resolve, reject) => {
    const w = window as unknown as { google?: { maps?: G }; __icgMaps?: () => void };
    if (w.google?.maps) return resolve(w.google.maps);
    w.__icgMaps = () => resolve(w.google!.maps);
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(KEY!)}&callback=__icgMaps&loading=async&language=pt-BR&region=BR`;
    s.async = true;
    s.onerror = () => reject(new Error("maps failed"));
    document.head.appendChild(s);
  });
  return loader;
}

function GoogleTripMap({ route, view, moved, recenter, onMoved, onFail }: MapProps & { onFail: () => void }) {
  const el = useRef<HTMLDivElement>(null);
  const g = useRef<{ maps: G; map: G; done: G; todo: G; car: G; end: G; fitted: boolean; auto: boolean } | null>(null);
  const [ready, setReady] = useState(false);
  const movedRef = useRef(moved);
  movedRef.current = moved;
  const onMovedRef = useRef(onMoved);
  onMovedRef.current = onMoved;

  useEffect(() => {
    let cancelled = false;
    loadGoogle()
      .then((maps) => {
        if (cancelled || !el.current) return;
        const map = new maps.Map(el.current, {
          center: view.driverLocation ?? route[0] ?? { lat: -23.5614, lng: -46.6559 }, zoom: 14,
          // The map fills the page and the page doesn't scroll, so one finger pans it.
          disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy", clickableIcons: false,
          styles: [{ featureType: "poi", stylers: [{ visibility: "off" }] }, { featureType: "transit", stylers: [{ visibility: "off" }] }],
        });
        const line = (color: string, weight: number) => new maps.Polyline({ map, strokeColor: color, strokeWeight: weight, strokeOpacity: 1, clickable: false });
        g.current = {
          maps, map, done: line(DONE, 5), todo: line(BRASA, 6), fitted: false, auto: false,
          car: new maps.Marker({ map, clickable: false, zIndex: 2 }),
          end: new maps.Marker({ map, clickable: false, icon: { path: "M-8 -8 H8 V8 H-8 Z", fillColor: INK, fillOpacity: 1, strokeColor: "#FFFFFF", strokeWeight: 3, scale: 1 } }),
        };
        // Moves the page makes itself (fit, follow) are flagged until the map settles; anything else is the viewer.
        map.addListener("dragstart", () => onMovedRef.current());
        map.addListener("zoom_changed", () => g.current?.auto || onMovedRef.current());
        map.addListener("idle", () => g.current && (g.current.auto = false));
        setReady(true);
      })
      .catch(() => !cancelled && onFail());
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fit = () => {
    const m = g.current!;
    const car = view.driverLocation;
    if (!route.length && !car) return;
    const b = new m.maps.LatLngBounds();
    for (const p of route) b.extend(p);
    if (car) b.extend(car);
    m.auto = true;
    m.map.fitBounds(b, 48);
    m.fitted = true;
  };

  useEffect(() => {
    if (ready && recenter) fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recenter]);

  useEffect(() => {
    const m = g.current;
    if (!ready || !m) return;
    const car = view.driverLocation;
    const { done, todo } = split(route, car, view.status);
    m.done.setPath(done);
    m.todo.setPath(todo);
    m.end.setPosition(route.at(-1) ?? null);
    m.end.setVisible(route.length > 0);
    m.car.setVisible(!!car && view.status !== "completed");
    if (car) {
      m.car.setPosition(car);
      const url = vehicleMarker(view.driver?.vehicle.kind, view.driver?.vehicle.color, car.heading);
      const size = markerSize(view.driver?.vehicle.kind);
      if (m.car.getIcon()?.url !== url) m.car.setIcon({ url, scaledSize: new m.maps.Size(size, size), anchor: new m.maps.Point(size / 2, size / 2) });
    }
    if (!m.fitted) fit();
    else if (car && !movedRef.current && view.status === "in_progress" && !m.map.getBounds()?.contains(car)) {
      m.auto = true;
      m.map.panTo(car);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, route, view.driverLocation, view.status, view.driver?.vehicle.color, view.driver?.vehicle.kind]);

  return <div ref={el} className="map-google" />;
}
